<template>
  <main id="page-profile" class="news-page">

    <!-- ── Masthead ─────────────────────────────── -->
    <header class="masthead masthead--compact">
      <div class="masthead__rule masthead__rule--thick"></div>
      <div class="masthead__rule masthead__rule--thin"></div>
    </header>

    <!-- ── Loading ────────────────────────────── -->
    <div v-if="!currentUser && loadingUser" class="profile__state">
      Se încarcă...
    </div>

    <!-- ── Not authenticated ──────────────────── -->
    <div v-else-if="!currentUser" class="profile__state">
      Trebuie să fii autentificat pentru a vedea profilul.
    </div>

    <template v-else>
      <!-- ── Date demografice lipsă (conturi Facebook/Google) ── -->
      <template v-if="currentUser.profile_incomplete">
        <h3 class="profile-section-title">Completează profilul</h3>
        <section class="profile-card">
          <p class="register-data-notice">Pentru corelarea statistică a răspunsurilor avem nevoie de genul și grupa ta de vârstă. Datele rămân anonime.</p>
          <form class="profile-edit-form" @submit.prevent="submitDemographics">
            <FormField v-if="!currentUser.gender" v-model="demographicsForm.gender" type="select" label="Gen" :options="genderOptions" :errors="demographicsErrors.gender" />
            <FormField v-if="!currentUser.age_group" v-model="demographicsForm.age_group" type="select" label="Grupă de vârstă" :options="ageGroupOptions" :errors="demographicsErrors.age_group" />
            <div v-if="demographicsError" class="auth-card__error">{{ demographicsError }}</div>
            <div class="profile-edit-form__actions">
              <button type="submit" class="profile-edit-form__save" :disabled="demographicsSubmitting">{{ demographicsSubmitting ? 'Se salvează...' : 'Salvează' }}</button>
            </div>
          </form>
        </section>
      </template>

      <!-- ── Account card ───────────────────────── -->
      <h3 class="profile-section-title">Detalii cont</h3>
      <section class="profile-card">
        <div class="profile-card__header-row">
          <h2 class="profile-card__title">{{ currentUser.username }}</h2>
          <button
            v-if="!isEditingData"
            type="button"
            class="profile-card__edit-btn"
            title="Editează informații"
            @click="openEditData"
          >
            <i class="mdi mdi-pencil" aria-hidden="true"></i>
          </button>
        </div>

        <!-- ── View mode ─────────────────────────── -->
        <template v-if="!isEditingData">
          <div class="profile-card__grid">
            <p v-if="currentUser.email" class="profile-card__item">
              <strong>Email</strong>
              <span>{{ currentUser.email }}</span>
            </p>
            <p class="profile-card__item">
              <strong>Gen</strong>
              <span>{{ currentUser.gender_label }}</span>
            </p>
            <p v-if="currentUser.age_group_label" class="profile-card__item">
              <strong>Vârstă</strong>
              <span>{{ currentUser.age_group_label }}</span>
            </p>
            <p v-if="currentUser.region_code" class="profile-card__item">
              <strong>Județ</strong>
              <span>{{ regionLabel(currentUser.region_code) }}</span>
            </p>
            <p v-if="currentUser.living_label" class="profile-card__item">
              <strong>Mediu</strong>
              <span>{{ currentUser.living_label }}</span>
            </p>
            <p v-if="currentUser.ocupation_name" class="profile-card__item">
              <strong>Ocupație</strong>
              <span>{{ currentUser.ocupation_name }}</span>
            </p>
            <p v-if="currentUser.education_name" class="profile-card__item">
              <strong>Educație</strong>
              <span>{{ currentUser.education_name }}</span>
            </p>
            <p v-if="currentUser.diaspora && currentUser.diaspora_name" class="profile-card__item">
              <strong>Diaspora</strong>
              <span>{{ currentUser.diaspora_name }}</span>
            </p>
            <p class="profile-card__item">
              <strong>Înregistrat la</strong>
              <span>{{ formattedJoinDate }}</span>
            </p>
          </div>
        </template>

        <!-- ── Edit mode ─────────────────────────── -->
        <template v-else>
          <form class="profile-edit-form" @submit.prevent="submitEditData">
            <FormField
              v-model="editDataForm.living"
              type="select"
              label="Locuiesc la"
              :options="livingOptions"
              :errors="editDataErrors.living"
            />

            <FormField
              v-model="editDataForm.region_code"
              type="select"
              label="Județ"
              :options="regionCodeOptions"
              :errors="editDataErrors.region_code"
            />

            <SearchLookupDropdown
              v-model="editDataForm.ocupation_id"
              endpoint="/lookups/ocupations"
              label="Ocupație"
              placeholder="Caută ocupația"
              :initial-name="editDataForm.ocupation_name"
            />

            <SearchLookupDropdown
              v-model="editDataForm.education_id"
              endpoint="/lookups/educations"
              label="Educație"
              placeholder="Caută nivelul de educație"
              :initial-name="editDataForm.education_name"
            />

            <!-- Diaspora checkbox -->
            <div class="form-field form-field--checkbox">
              <label class="form-field__checkbox-label">
                <input
                  v-model="editDataForm.diaspora"
                  type="checkbox"
                  class="form-field__checkbox"
                />
                <span>Diaspora</span>
              </label>
            </div>

            <SearchLookupDropdown
              v-if="editDataForm.diaspora"
              v-model="editDataForm.diaspora_id"
              endpoint="/lookups/diaspora"
              label="Țara / regiunea (diaspora)"
              placeholder="Caută sau adaugă țara / reședința..."
              :initial-name="editDataForm.diaspora_name"
            />

            <div v-if="editDataFeedback" class="auth-card__message">{{ editDataFeedback }}</div>
            <div v-if="editDataError" class="auth-card__error">{{ editDataError }}</div>

            <div class="profile-edit-form__actions">
              <button type="submit" class="profile-edit-form__save" :disabled="editDataSubmitting">
                {{ editDataSubmitting ? 'Se salvează...' : 'Salvează' }}
              </button>
              <button type="button" class="profile-edit-form__cancel" @click="isEditingData = false">
                Anulează
              </button>
            </div>
          </form>
        </template>
      </section>

      <!-- ── Tabs ───────────────────────────────── -->
      <div class="profile-tabs">
        <button
          type="button"
          class="profile-tabs__tab"
          :class="{ 'profile-tabs__tab--active': activeTab === 'my-polls' }"
          @click="switchTab('my-polls')"
        >
          Sondajele mele
        </button>
        <button
          type="button"
          class="profile-tabs__tab"
          :class="{ 'profile-tabs__tab--active': activeTab === 'participations' }"
          @click="switchTab('participations')"
        >
          Participări
        </button>
      </div>

      <!-- ── My polls tab ───────────────────────── -->
      <section v-if="activeTab === 'my-polls'" class="profile-tab-panel">
        <div class="profile__search-row">
          <input
            v-model="pollsSearch"
            type="search"
            class="form-field__input profile__search-input"
            placeholder="Caută în sondajele mele..."
            @input="onPollsSearchInput"
          />
        </div>

        <template v-if="pollsLoading">
          <div v-for="n in 3" :key="'skel-' + n" class="poll-skeleton">
            <div class="poll-skeleton__line poll-skeleton__title"></div>
            <div class="poll-skeleton__line poll-skeleton__title-short"></div>
            <div class="poll-skeleton__line poll-skeleton__meta"></div>
          </div>
        </template>

        <div v-else-if="pollsError" class="profile__state profile__state--error">
          {{ pollsError }}
        </div>

        <div v-else-if="myPolls.length === 0" class="profile__state">
          Nu ai creat niciun sondaj încă.
        </div>

        <template v-else>
          <div
            v-for="poll in myPolls"
            :key="poll.id"
            class="poll-card card--liftable profile-tab-panel__card"
            role="button"
            tabindex="0"
            @click="$router.push({ name: 'polls.show', params: { id: poll.id, slug: poll.slug } })"
            @keydown.enter="$router.push({ name: 'polls.show', params: { id: poll.id, slug: poll.slug } })"
          >
            <div class="poll-card__header">
              <h3 class="poll-card__title">{{ poll.title }}</h3>
            </div>
            <div class="poll-card__meta">
              <span class="poll-card__meta-item">{{ votesLabel(poll) }}</span>
              <span class="poll-card__meta-sep">·</span>
              <span class="poll-card__meta-item">{{ optionsLabel(poll) }}</span>
              <span class="poll-card__meta-sep">·</span>
              <span class="poll-card__meta-item">{{ formatDate(poll.created_at) }}</span>
            </div>
          </div>

          <div v-if="pollsLastPage > 1" class="pagination">
            <button
              type="button"
              class="pagination__btn"
              :disabled="pollsPage === 1"
              @click="goToPollsPage(pollsPage - 1)"
            >
              <i class="mdi mdi-chevron-left" aria-hidden="true"></i> Anterior
            </button>
            <span class="pagination__info">Pagina {{ pollsPage }} din {{ pollsLastPage }}</span>
            <button
              type="button"
              class="pagination__btn"
              :disabled="pollsPage === pollsLastPage"
              @click="goToPollsPage(pollsPage + 1)"
            >
              Următor <i class="mdi mdi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </template>
      </section>

      <!-- ── Participations tab ─────────────────── -->
      <section v-if="activeTab === 'participations'" class="profile-tab-panel">
        <div class="profile__search-row">
          <input
            v-model="participationsSearch"
            type="search"
            class="form-field__input profile__search-input"
            placeholder="Caută în participări..."
            @input="onParticipationsSearchInput"
          />
        </div>

        <template v-if="participationsLoading">
          <div v-for="n in 3" :key="'skel-p-' + n" class="poll-skeleton">
            <div class="poll-skeleton__line poll-skeleton__title"></div>
            <div class="poll-skeleton__line poll-skeleton__title-short"></div>
            <div class="poll-skeleton__line poll-skeleton__meta"></div>
          </div>
        </template>

        <div v-else-if="participationsError" class="profile__state profile__state--error">
          {{ participationsError }}
        </div>

        <div v-else-if="participations.length === 0" class="profile__state">
          Nu ai participat la niciun sondaj încă.
        </div>

        <template v-else>
          <div
            v-for="item in participations"
            :key="item.poll_id"
            class="participation-card card--liftable"
            role="button"
            tabindex="0"
            @click="$router.push({ name: 'polls.show', params: { id: item.poll_id, slug: item.poll_slug } })"
            @keydown.enter="$router.push({ name: 'polls.show', params: { id: item.poll_id, slug: item.poll_slug } })"
          >
            <h3 class="participation-card__question">{{ item.poll_title }}</h3>
            <div class="participation-card__options">
              <span
                v-for="option in item.options"
                :key="option.id"
                class="participation-card__badge"
                :class="{ 'participation-card__badge--voted': option.id === item.voted_option_id }"
              >
                <span v-if="option.id === item.voted_option_id" class="participation-card__badge-check"><i class="mdi mdi-check" aria-hidden="true"></i></span>
                {{ option.title }}
              </span>
            </div>
            <div class="participation-card__meta">
              {{ formatDate(item.voted_at) }}
            </div>
          </div>

          <div v-if="participationsLastPage > 1" class="pagination">
            <button
              type="button"
              class="pagination__btn"
              :disabled="participationsPage === 1"
              @click="goToParticipationsPage(participationsPage - 1)"
            >
              <i class="mdi mdi-chevron-left" aria-hidden="true"></i> Anterior
            </button>
            <span class="pagination__info">Pagina {{ participationsPage }} din {{ participationsLastPage }}</span>
            <button
              type="button"
              class="pagination__btn"
              :disabled="participationsPage === participationsLastPage"
              @click="goToParticipationsPage(participationsPage + 1)"
            >
              Următor <i class="mdi mdi-chevron-right" aria-hidden="true"></i>
            </button>
          </div>
        </template>
      </section>
    </template>

  </main>
</template>

<script>
import authMixin from '../../mixins/authMixin.js';
import recaptchaMixin from '../../mixins/recaptchaMixin.js';
import FormField from '../../components/FormField.vue';
import SearchLookupDropdown from '../../components/SearchLookupDropdown.vue';

export default {
  name: 'ProfileIndexView',

  components: {
    FormField,
    SearchLookupDropdown,
  },

  mixins: [authMixin, recaptchaMixin],

  data() {
    return {
      loadingUser: true,
      activeTab: 'my-polls',

      // Profile card edit toggle
      isEditingData: false,

      // My polls tab
      myPolls: [],
      pollsLoading: false,
      pollsError: null,
      pollsPage: 1,
      pollsLastPage: 1,
      pollsSearch: '',
      pollsDebouncedSearch: '',
      pollsDebounceTimer: null,

      // Participations tab
      participations: [],
      participationsLoading: false,
      participationsError: null,
      participationsPage: 1,
      participationsLastPage: 1,
      participationsSearch: '',
      participationsDebouncedSearch: '',
      participationsDebounceTimer: null,

      // Date demografice lipsă (conturi Facebook/Google)
      demographicsForm: {
        gender: '',
        age_group: '',
      },
      demographicsSubmitting: false,
      demographicsError: '',
      demographicsErrors: {},
      genderOptions: [
        { value: '', label: '— Selectează gen —' },
        { value: 'm', label: 'Bărbat' },
        { value: 'f', label: 'Femeie' },
        { value: 'n', label: 'Nespecificat' },
      ],
      ageGroupOptions: [
        { value: '', label: '— Selectează —' },
        { value: 'under_14', label: 'Sub 14 ani' },
        { value: '14_18', label: '14 – 18 ani' },
        { value: '18_24', label: '18 – 24 ani' },
        { value: '24_30', label: '24 – 30 ani' },
        { value: '30_40', label: '30 – 40 ani' },
        { value: '40_65', label: '40 – 65 ani' },
        { value: 'over_65', label: 'Peste 65 ani' },
      ],

      // Edit data form
      editDataForm: {
        region_code: '',
        living: '',
        ocupation_id: null,
        ocupation_name: '',
        education_id: null,
        education_name: '',
        diaspora: false,
        diaspora_id: null,
        diaspora_name: '',
      },
      editDataSubmitting: false,
      editDataFeedback: '',
      editDataError: '',
      editDataErrors: {},

      livingOptions: [
        { value: '', label: '— Selectează —' },
        { value: 'city', label: 'Oraș' },
        { value: 'country', label: 'Țară' },
      ],
      regionCodeOptions: [
        { value: '', label: '— Selectează județ —' },
        { value: 'AB', label: 'AB – Alba' },
        { value: 'AG', label: 'AG – Argeș' },
        { value: 'AR', label: 'AR – Arad' },
        { value: 'B', label: 'B – București' },
        { value: 'BC', label: 'BC – Bacău' },
        { value: 'BH', label: 'BH – Bihor' },
        { value: 'BN', label: 'BN – Bistrița-Năsăud' },
        { value: 'BR', label: 'BR – Brăila' },
        { value: 'BT', label: 'BT – Botoșani' },
        { value: 'BV', label: 'BV – Brașov' },
        { value: 'BZ', label: 'BZ – Buzău' },
        { value: 'CJ', label: 'CJ – Cluj' },
        { value: 'CL', label: 'CL – Călărași' },
        { value: 'CS', label: 'CS – Caraș-Severin' },
        { value: 'CT', label: 'CT – Constanța' },
        { value: 'CV', label: 'CV – Covasna' },
        { value: 'DB', label: 'DB – Dâmbovița' },
        { value: 'DJ', label: 'DJ – Dolj' },
        { value: 'GJ', label: 'GJ – Gorj' },
        { value: 'GL', label: 'GL – Galați' },
        { value: 'GR', label: 'GR – Giurgiu' },
        { value: 'HD', label: 'HD – Hunedoara' },
        { value: 'HR', label: 'HR – Harghita' },
        { value: 'IF', label: 'IF – Ilfov' },
        { value: 'IL', label: 'IL – Ialomița' },
        { value: 'IS', label: 'IS – Iași' },
        { value: 'MH', label: 'MH – Mehedinți' },
        { value: 'MM', label: 'MM – Maramureș' },
        { value: 'MS', label: 'MS – Mureș' },
        { value: 'NT', label: 'NT – Neamț' },
        { value: 'OT', label: 'OT – Olt' },
        { value: 'PH', label: 'PH – Prahova' },
        { value: 'SB', label: 'SB – Sibiu' },
        { value: 'SJ', label: 'SJ – Sălaj' },
        { value: 'SM', label: 'SM – Satu Mare' },
        { value: 'SV', label: 'SV – Suceava' },
        { value: 'TL', label: 'TL – Tulcea' },
        { value: 'TM', label: 'TM – Timiș' },
        { value: 'TR', label: 'TR – Teleorman' },
        { value: 'VL', label: 'VL – Vâlcea' },
        { value: 'VN', label: 'VN – Vrancea' },
        { value: 'VS', label: 'VS – Vaslui' },
      ],
    };
  },

  computed: {
    formattedJoinDate() {
      if (!this.currentUser?.created_at) return '—';
      return new Date(this.currentUser.created_at).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },

  async mounted() {
    this.initFromQuery();
    await this.fetchCurrentUser();
    this.loadingUser = false;
    if (this.currentUser) {
      if (this.activeTab === 'participations') {
        this.fetchParticipations();
      } else {
        this.fetchMyPolls();
      }
    }
  },

  beforeUnmount() {
    clearTimeout(this.pollsDebounceTimer);
    clearTimeout(this.participationsDebounceTimer);
  },

  methods: {
    initFromQuery() {
      const { tab, page, search } = this.$route.query;
      if (tab === 'participations') {
        this.activeTab = 'participations';
        this.participationsPage = parseInt(page, 10) || 1;
        if (search) {
          this.participationsSearch = search;
          this.participationsDebouncedSearch = search;
        }
      } else {
        this.activeTab = 'my-polls';
        this.pollsPage = parseInt(page, 10) || 1;
        if (search) {
          this.pollsSearch = search;
          this.pollsDebouncedSearch = search;
        }
      }
    },

    updateQuery() {
      const query = { tab: this.activeTab };
      if (this.activeTab === 'my-polls') {
        if (this.pollsPage > 1) query.page = this.pollsPage;
        if (this.pollsDebouncedSearch) query.search = this.pollsDebouncedSearch;
      } else if (this.activeTab === 'participations') {
        if (this.participationsPage > 1) query.page = this.participationsPage;
        if (this.participationsDebouncedSearch) query.search = this.participationsDebouncedSearch;
      }
      this.$router.replace({ query }).catch(() => {});
    },

    switchTab(tab) {
      this.activeTab = tab;
      this.updateQuery();
      if (tab === 'my-polls' && this.myPolls.length === 0 && !this.pollsLoading) {
        this.fetchMyPolls();
      }
      if (tab === 'participations' && this.participations.length === 0 && !this.participationsLoading) {
        this.fetchParticipations();
      }
    },

    openEditData() {
      this.initEditDataForm();
      this.isEditingData = true;
    },

    onPollsSearchInput() {
      clearTimeout(this.pollsDebounceTimer);
      this.pollsDebounceTimer = setTimeout(() => {
        this.pollsDebouncedSearch = this.pollsSearch;
        this.pollsPage = 1;
        this.updateQuery();
        this.fetchMyPolls();
      }, 400);
    },

    async fetchMyPolls() {
      this.pollsLoading = true;
      this.pollsError = null;
      try {
        const params = { page: this.pollsPage };
        if (this.pollsDebouncedSearch) params.search = this.pollsDebouncedSearch;
        const response = await window.axios.get('/profile/polls', { params });
        this.myPolls = response.data.data;
        this.pollsPage = response.data.current_page;
        this.pollsLastPage = response.data.last_page;
      } catch {
        this.pollsError = 'Nu s-au putut încărca sondajele. Încearcă din nou.';
      } finally {
        this.pollsLoading = false;
      }
    },

    async goToPollsPage(page) {
      this.pollsPage = page;
      this.updateQuery();
      await this.fetchMyPolls();
    },

    onParticipationsSearchInput() {
      clearTimeout(this.participationsDebounceTimer);
      this.participationsDebounceTimer = setTimeout(() => {
        this.participationsDebouncedSearch = this.participationsSearch;
        this.participationsPage = 1;
        this.updateQuery();
        this.fetchParticipations();
      }, 400);
    },

    async fetchParticipations() {
      this.participationsLoading = true;
      this.participationsError = null;
      try {
        const params = { page: this.participationsPage };
        if (this.participationsDebouncedSearch) params.search = this.participationsDebouncedSearch;
        const response = await window.axios.get('/profile/participations', { params });
        this.participations = response.data.data;
        this.participationsPage = response.data.current_page;
        this.participationsLastPage = response.data.last_page;
      } catch {
        this.participationsError = 'Nu s-au putut încărca participările. Încearcă din nou.';
      } finally {
        this.participationsLoading = false;
      }
    },

    async goToParticipationsPage(page) {
      this.participationsPage = page;
      this.updateQuery();
      await this.fetchParticipations();
    },

    // Maps a 2-letter region code to its full county name.
    // Relies on regionCodeOptions labels having the format 'CODE – Name'.
    regionLabel(code) {
      const opt = this.regionCodeOptions.find(o => o.value === code);
      if (!opt) return code;
      // label format: 'AB – Alba'
      const dashIdx = opt.label.indexOf(' – ');
      return dashIdx !== -1 ? opt.label.slice(dashIdx + 3) : opt.label;
    },

    votesLabel(poll) {
      const n = poll.total_votes || 0;
      return n === 1 ? '1 vot' : `${n} voturi`;
    },

    optionsLabel(poll) {
      const n = poll.options_count || 0;
      return n === 1 ? '1 opțiune' : `${n} opțiuni`;
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },

    async submitDemographics() {
      this.demographicsSubmitting = true;
      this.demographicsError      = '';
      this.demographicsErrors     = {};

      try {
        const response = await window.axios.put('/profile/demographics', this.demographicsForm);
        this.currentUser = response.data?.user || this.currentUser;
      } catch (error) {
        const responseErrors = error?.response?.data?.errors;
        if (responseErrors && typeof responseErrors === 'object') {
          this.demographicsErrors = responseErrors;
          const first = Object.values(responseErrors)[0];
          this.demographicsError = Array.isArray(first) ? first[0] : 'A apărut o eroare.';
        } else {
          this.demographicsError = error?.response?.data?.message || 'Salvarea a eșuat.';
        }
      } finally {
        this.demographicsSubmitting = false;
      }
    },

    initEditDataForm() {
      const u = this.currentUser;
      if (!u) return;
      this.editDataForm = {
        region_code:    u.region_code    ?? '',
        living:         u.living         ?? '',
        ocupation_id:   u.ocupation_id   ?? null,
        ocupation_name: u.ocupation_name ?? '',
        education_id:   u.education_id   ?? null,
        education_name: u.education_name ?? '',
        diaspora:       !!u.diaspora,
        diaspora_id:    u.diaspora_id    ?? null,
        diaspora_name:  u.diaspora_name  ?? '',
      };
      this.editDataFeedback = '';
      this.editDataError    = '';
      this.editDataErrors   = {};
    },

    async submitEditData() {
      this.editDataSubmitting = true;
      this.editDataFeedback   = '';
      this.editDataError      = '';
      this.editDataErrors     = {};

      const recaptchaToken = await this.getRecaptchaToken('profile');
      const payload = {
        ...this.editDataForm,
        region_code:  this.editDataForm.region_code === '' ? null : this.editDataForm.region_code,
        living:       this.editDataForm.living === '' ? null : this.editDataForm.living,
        diaspora_id:  this.editDataForm.diaspora ? this.editDataForm.diaspora_id : null,
        recaptcha_token: recaptchaToken,
      };

      try {
        const response = await window.axios.put('/profile/data', payload);
        this.currentUser    = response.data?.user || this.currentUser;
        this.editDataFeedback = response.data?.message || 'Datele au fost actualizate.';
        // Close edit form after user sees the feedback message
        const feedbackDelay = 1200;
        setTimeout(() => {
          this.isEditingData = false;
        }, feedbackDelay);
      } catch (error) {
        const responseErrors = error?.response?.data?.errors;
        if (responseErrors && typeof responseErrors === 'object') {
          this.editDataErrors = responseErrors;
          const first = Object.values(responseErrors)[0];
          this.editDataError = Array.isArray(first) ? first[0] : 'A apărut o eroare.';
        } else {
          this.editDataError = error?.response?.data?.message || 'Salvarea a eșuat.';
        }
      } finally {
        this.editDataSubmitting = false;
      }
    },
  },
};
</script>

